import { ZodIssue, ZodSchema } from "zod";
import { FormState } from "@/app/features/shared/form-builder/types/types";
import { defaultFormState } from "@/app/features/shared/form-builder/types/defaults";
import { disableDraftMode, enableDraftMode } from "@/app/lib/actions/editing";
import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { AutosaveState, useAutosave } from "@/app/lib/hooks/useAutosave";
import { PublicationStatus } from "@/app/lib/generated/graphql-types";
import { toast } from "react-toastify";
import { deepMerge, DeepPartial } from "@/app/features/shared/__to_be_integrated/types/forms/deepMerge";
import {
    editableFromGraphQl,
    inputFromEditable
} from "@/app/lib/hooks/use-editor/schema-mapper";
import {
    PublishOptions,
    showPublishResultToast,
} from "@/app/lib/hooks/use-editor/publish-result";

function normalizeFieldErrors(validationIssues: ZodIssue[]): Record<string, string[]> {
    return validationIssues.reduce<Record<string, string[]>>((acc, issue) => {
        const key = issue.path.length > 0 ? issue.path.join('.') : '_root';
        const next = acc[key] ?? [];
        next.push(issue.message);
        acc[key] = next;
        return acc;
    }, {});
}

function extractHasChanges(value: unknown): boolean {
    if (!value || typeof value !== 'object' || !('hasChanges' in value)) {
        return false;
    }

    return Boolean((value as { hasChanges?: unknown }).hasChanges);
}

type UseCollectionTypeEditorConfig<TEntity, TEditable, TInput> = {
    documentId: string | 'new';
    schema: ZodSchema<TEditable>;
    defaultEntity?: TEditable;

    loadEntity: ({ documentId, status }: { documentId: string, status?: PublicationStatus }) => Promise<{ data?: TEntity, error?: Error }>;
    preCreate?: (formData: TEditable) => Promise<{ data?: Partial<TEditable>, error?: Error } | null>;
    create?: (input: TInput, status: PublicationStatus) => Promise<{ error?: Error }>;
    prePublish?: (formData: TEditable) => Promise<{ data?: Partial<TEditable>, error?: Error } | null>;
    update: ({ documentId, data, status }: { documentId: string, data: TInput, status: PublicationStatus }) => Promise<{ error?: Error }>;
    toEditable?: (entity: TEntity) => TEditable;
    toInput?: (formData: TEditable) => Promise<TInput> | TInput;

    autosave?: (documentId: string, input: TInput) => Promise<{ error?: Error }>;
    refreshEventName?: string;
    manageDraftMode?: boolean;
}

export type UseCollectionTypeEditorType<TEditable> = {
    isLoading: boolean;
    autosaveState: AutosaveState | null;
    error: string | undefined;
    publish: (options?: PublishOptions) => Promise<boolean>;
    editable: TEditable | undefined;
    updateEditable: (partialEditable: DeepPartial<TEditable>) => void;
    editingState: FormState;
    updateEditingState: (partialEditingState: Partial<FormState>) => void;
    resetForm: () => void;
    discardChanges: () => Promise<void>;
    hasChanges: boolean;
    shouldAutosave: boolean;
    hasValidationErrors?: boolean;
    errorForField?: (field: string) => string | null;
}

export function useCollectionTypeEditor<TEntity, TEditable, TInput>({
    documentId,
    schema,
    defaultEntity,
    loadEntity,
    update,
    create,
    prePublish,
    preCreate,
    toEditable,
    toInput,
    autosave,
    refreshEventName,
    manageDraftMode = true
}: UseCollectionTypeEditorConfig<TEntity, TEditable, TInput>): UseCollectionTypeEditorType<TEditable> {

    const mapToEditable = useMemo(
        () => toEditable ?? ((entity: TEntity) => editableFromGraphQl(entity, schema)),
        [toEditable, schema]
    );
    const mapToInput = useMemo(
        () => toInput ?? ((editable: TEditable) => inputFromEditable<TEditable, TInput>(editable, schema)),
        [toInput, schema]
    );

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [editable, setEditable] = useState<TEditable | undefined>(undefined)
    const [editingState, setEditingState] = useState<FormState>(defaultFormState);
    const [validationIssues, setValidationIssues] = useState<ZodIssue[]>([]);
    const [serverHasChanges, setServerHasChanges] = useState<boolean>(false);
    const [isDirty, setIsDirty] = useState(false);
    const deferredEditable = useDeferredValue(editable);
    const initialEditedEntityRef = useRef<TEditable | undefined>(undefined);
    const validationSignatureRef = useRef<string | undefined>(undefined);
    const publishInFlightRef = useRef<Promise<boolean> | null>(null);
    const resetSuccessTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const updateEditable = useCallback((partialEditable: DeepPartial<TEditable>) => {
        setIsDirty(true);
        setEditable((prev) => deepMerge(prev, partialEditable));
    }, []);

    const updateEditingState = useCallback((partialEditingState: Partial<FormState>) => {
        setEditingState((prev: FormState) => ({
            ...prev,
            ...partialEditingState
        }))
    }, []);

    const resetForm = useCallback(() => {
        initialEditedEntityRef.current = undefined;
        setIsDirty(false);
        setEditable(undefined);
        setEditingState(defaultFormState);
    }, []);

    const shouldAutosave = useMemo(() => {
        if (documentId === 'new') {
            return false;
        }
        if (editable === undefined) {
            return false;
        }
        if (!autosave) {
            return false;
        }

        return (editingState.isValid ?? false) && isDirty;
    }, [documentId, autosave, editingState.isValid, editable, isDirty]);


    const { autosaveState, error: autosaveError } = useAutosave({
        enabled: shouldAutosave,
        guard: () => editable !== undefined && schema.safeParse(editable).success,
        saveHandler: async () => {
            if (!editable) return;
            if (!autosave) return;

            const input = await mapToInput(editable);
            const { error } = await autosave(documentId, input);

            if (error) throw error;
        }
    }, [deferredEditable])

    // enable draft mode while mounted
    useEffect(() => {
        if (!manageDraftMode) return;

        enableDraftMode();
        return () => {
            disableDraftMode();
        }
    }, [manageDraftMode]);

    useEffect(() => {
        return () => { resetForm() };
    }, [resetForm]);

    // data loading
    useEffect(() => {
        let cancelled = false;

        (async () => {
            setIsLoading(true);

            try {
                if (documentId === 'new') {
                    if (!cancelled) {
                        initialEditedEntityRef.current = defaultEntity;
                        setIsDirty(false);
                        setEditable(defaultEntity)
                    }
                    return;
                }

                const { data, error } = await loadEntity({ documentId, status: PublicationStatus.Draft });
                if (!cancelled) {
                    if (error) {
                        updateEditingState({ error: error.message })
                        return;
                    } else if (!data) {
                        updateEditingState({ error: "Neither Error nor Data is present, this shouldn't happen!" })
                        return;
                    } else {
                        setServerHasChanges(extractHasChanges(data));
                        const editable = mapToEditable(data);
                        initialEditedEntityRef.current = editable;
                        setIsDirty(false);
                        setEditable(editable)
                    }
                }
            } catch (error) {
                if (!cancelled) updateEditingState({ error: (error as Error).message })
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        })()

        return () => { cancelled = true; };
    }, [documentId, defaultEntity, loadEntity, mapToEditable, updateEditingState]);

    // live validation
    useEffect(() => {
        if (deferredEditable === undefined) {
            return;
        }

        const validation = schema.safeParse(deferredEditable);
        const signature = validation.success
            ? "valid"
            : JSON.stringify(validation.error.issues.map(({ path, code, message }) => ({ path, code, message })));

        if (validationSignatureRef.current === signature) return;
        validationSignatureRef.current = signature;

        if (!validation.success) {
            const normalizedFieldErrors = normalizeFieldErrors(validation.error.issues);
            setValidationIssues(validation.error.issues);
            updateEditingState({
                isValid: false,
                fieldErrors: normalizedFieldErrors,
                error: validation.error.message,
            });
        } else {
            setValidationIssues([]);
            updateEditingState({
                isValid: true,
                fieldErrors: null,
                error: null
            });
        }
    }, [deferredEditable, schema, updateEditingState]);

    const onDiscardChanges = useCallback(async () => {
        if (documentId === 'new') {
            return;
        }

        try {
            const { data, error } = await loadEntity({ documentId, status: PublicationStatus.Published });

            if (error) {
                console.error('error while fetch published', error);
                toast.error("Oops, da ist etwas schief gelaufen! Änderungen konnten nicht verworfen werden.")
                return;
            }

            if (!data) {
                toast.error("Keine veröffentlichte Version gefunden. Änderungen konnten nicht verworfen werden.")
                return;
            }

            const publishedEditable = mapToEditable(data);
            initialEditedEntityRef.current = publishedEditable;
            setIsDirty(false);
            setServerHasChanges(false);
            setEditable(publishedEditable);
            updateEditingState({
                isValid: true,
                fieldErrors: null,
                error: null,
                success: null,
                isSubmitting: false
            });
            toast.success("Änderungen verworfen.")
        } catch (error) {
            console.error('caught error while discarding', JSON.stringify(error, null, 2));
            toast.error("Oops, da ist etwas schief gelaufen! Änderungen konnten nicht verworfen werden.")
        }
    }, [documentId, loadEntity, mapToEditable, updateEditingState]);

    const clearResetSuccessTimeout = useCallback(() => {
        if (resetSuccessTimeoutRef.current) {
            clearTimeout(resetSuccessTimeoutRef.current);
            resetSuccessTimeoutRef.current = null;
        }
    }, []);

    const scheduleResetSuccessState = useCallback(() => {
        clearResetSuccessTimeout();
        resetSuccessTimeoutRef.current = setTimeout(() => {
            updateEditingState({ success: null });
        }, 3000);
    }, [clearResetSuccessTimeout, updateEditingState]);

    useEffect(() => {
        return () => {
            clearResetSuccessTimeout();
        };
    }, [clearResetSuccessTimeout]);

    const publishBase = useCallback(async (): Promise<boolean> => {
        if (!editable) return false;
        let mutableFormData = editable;
        updateEditingState({ isSubmitting: true });

        try {
            let error;
            if (documentId === 'new') {
                if (!create) {
                    throw new Error('documentId is `new` but no create function is provided. Please provide create function .');
                }
                if (preCreate) {
                    const result = await preCreate(editable);
                    if (result) {
                        const { data: partialFormData, error: preCreateError } = result;
                        error = preCreateError;

                        if (!preCreateError && partialFormData) {
                            mutableFormData = { ...mutableFormData, ...partialFormData };
                        }
                    }
                }
                if (!error) {
                    const input = await mapToInput(mutableFormData);
                    const { error: createError } = await create(input, PublicationStatus.Published);
                    error = createError;
                }
            } else {
                if (prePublish) {
                    const result = await prePublish(editable);
                    if (result) {
                        const { data: partialFormData, error: prePublishError } = result;
                        error = prePublishError;

                        if (!prePublishError && partialFormData) {
                            mutableFormData = { ...mutableFormData, ...partialFormData };
                        }
                    }
                }

                if (!error) {
                    const data = await mapToInput(mutableFormData);
                    const { error: updateError } = await update({ documentId, data, status: PublicationStatus.Published });
                    error = updateError;
                }
            }

            if (error) {
                console.error('error while submitting', JSON.stringify(error, null, 2));
                updateEditingState({
                    success: false,
                    error: error.message,
                    isSubmitting: false
                })
                return false;
            } else {
                initialEditedEntityRef.current = mutableFormData;
                setIsDirty(false);
                setServerHasChanges(false);
                setEditable(mutableFormData);
                updateEditingState({
                    success: true,
                    isSubmitting: false,
                    error: null,
                });
                if (refreshEventName) {
                    window.dispatchEvent(new Event(refreshEventName))
                }
                if (manageDraftMode) {
                    disableDraftMode();
                }
                return true;
            }
        } catch (error) {
            console.error('caught error while submitting', JSON.stringify(error, null, 2));
            updateEditingState({
                success: false,
                error: (error as Error).message,
                isSubmitting: false
            })
            return false;
        } finally {
            scheduleResetSuccessState();
        }
    }, [documentId, editable, updateEditingState, preCreate, mapToInput, create, prePublish, update, refreshEventName, scheduleResetSuccessState, manageDraftMode]);

    const publish = useCallback((options: PublishOptions = {}): Promise<boolean> => {
        if (publishInFlightRef.current) {
            return publishInFlightRef.current;
        }

        const operation = (async () => {
            const succeeded = await publishBase();
            if (options.notify !== false) {
                showPublishResultToast(succeeded);
            }
            return succeeded;
        })();

        publishInFlightRef.current = operation;
        const clearInFlight = () => {
            if (publishInFlightRef.current === operation) {
                publishInFlightRef.current = null;
            }
        };
        void operation.then(clearInFlight, clearInFlight);

        return operation;
    }, [publishBase]);

    const errorForField = useCallback((field: string): string | null => {
        const messages = validationIssues
            .filter((issue) => issue.path.join('.') === field)
            .map((issue) => issue.message);

        return messages.length > 0 ? messages.join('\n') : null;
    }, [validationIssues]);

    return {
        isLoading,
        editable,
        updateEditable,
        editingState,
        updateEditingState,
        autosaveState,
        error: autosaveError || editingState.error || undefined,
        hasChanges: serverHasChanges || isDirty,
        publish,
        resetForm,
        discardChanges: onDiscardChanges,
        shouldAutosave,
        hasValidationErrors: validationIssues.length > 0,
        errorForField,
    }
}
